'use client'

import React from 'react'

import { Box, HTMLChakraProps, Text } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { ReplySubmitIcon } from '../Icon'
import {
  questionCardAnswerLabelStyles,
  questionCardAnswerListStyles,
  questionCardAnswerOptionStyles,
  questionCardQuestionStyles,
  questionCardRootStyles,
  questionCardShortcutStyles,
  questionCardStyles,
  questionCardSubmitIconStyles,
  questionCardSubtitleStyles,
} from './QuestionCard.styles'

export type QuestionCardAnswerOption = {
  id: string
  label: string
  shortcutLabel?: string
}

export type QuestionCardProps = Omit<HTMLChakraProps<'div'>, 'onSelect'> & {
  colorScheme?: ColorScheme
  question: string
  answerOptions: QuestionCardAnswerOption[]
  onSelectAnswer?: (answerId: string) => void
  selectedAnswerId?: string
  subtitle?: string
}

const SubmitIndicatorIcon = ({ colorScheme }: { colorScheme: ColorScheme }) => (
  <Box aria-hidden='true' {...questionCardSubmitIconStyles(colorScheme)}>
    <ReplySubmitIcon />
  </Box>
)

const handleAnswerKeyDown = (
  event: React.KeyboardEvent<HTMLDivElement>,
  answerId: string,
  onSelectAnswer?: (answerId: string) => void
) => {
  if (!onSelectAnswer) return

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    onSelectAnswer(answerId)
  }
}

export const QuestionCard = ({
  answerOptions,
  colorScheme = 'dark',
  onSelectAnswer,
  question,
  selectedAnswerId,
  subtitle,
  ...rest
}: QuestionCardProps) => {
  const isInteractive = Boolean(onSelectAnswer)

  return (
    <Box {...questionCardRootStyles} {...rest}>
      <Box {...questionCardStyles(colorScheme)}>
        <Box>
          <Text as='h3' {...questionCardQuestionStyles(colorScheme)}>
            {question}
          </Text>
          {subtitle && (
            <Text {...questionCardSubtitleStyles(colorScheme)}>{subtitle}</Text>
          )}
        </Box>

        <Box {...questionCardAnswerListStyles}>
          {answerOptions.map((answer, index) => {
            const isSelected = answer.id === selectedAnswerId
            const shortcutLabel = answer.shortcutLabel ?? String(index + 1)

            return (
              <Box
                aria-disabled={!isInteractive}
                aria-pressed={isSelected}
                key={answer.id}
                onClick={() => onSelectAnswer?.(answer.id)}
                onKeyDown={(event) =>
                  handleAnswerKeyDown(event, answer.id, onSelectAnswer)
                }
                role='button'
                tabIndex={isInteractive ? 0 : -1}
                {...questionCardAnswerOptionStyles(
                  isSelected,
                  colorScheme,
                  isInteractive
                )}
              >
                <Box {...questionCardShortcutStyles(isSelected, colorScheme)}>
                  {shortcutLabel}
                </Box>
                <Text {...questionCardAnswerLabelStyles}>{answer.label}</Text>
                {isSelected && (
                  <SubmitIndicatorIcon colorScheme={colorScheme} />
                )}
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}
